/**
 * ConsentPop Widget - Entry Point
 *
 * Usage:
 *   <script src="https://cdn.consentpop.com/prod/consentpop.min.js" data-api-key="YOUR_KEY" defer></script>
 *
 * Or via window config:
 *   <script>window.ConsentPopConfig = { apiKey: 'YOUR_KEY' };</script>
 *   <script src="https://cdn.consentpop.com/prod/consentpop.min.js" defer></script>
 */

import cookieConsentCss from 'vanilla-cookieconsent/dist/cookieconsent.css?inline';
import { ConsentPop } from './core/ConsentPop';
import { GtagIntegration } from './core/GtagIntegration';
import type { ConsentPopInitConfig } from './core/types';

const BASE_STYLE_ID = 'consentpop-base-styles';
let activeInstance: ConsentPop | null = null;
let pendingPreferencesOpen = false;

type ConsentPopGlobal = typeof ConsentPop & {
  showPreferences: () => boolean;
  isInitialized: () => boolean;
};

const globalConsentPop = ConsentPop as ConsentPopGlobal;
globalConsentPop.showPreferences = () => {
  const opened = activeInstance?.showPreferences() ?? false;
  if (!opened) pendingPreferencesOpen = true;
  return opened;
};
globalConsentPop.isInitialized = () => activeInstance?.isInitialized() ?? false;

function injectBaseStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(BASE_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = BASE_STYLE_ID;
  style.textContent = cookieConsentCss;
  document.head.appendChild(style);
}

function configFromScript(script: HTMLScriptElement | null): ConsentPopInitConfig | undefined {
  const apiKey = script?.getAttribute('data-api-key');
  if (!script || !apiKey) return undefined;

  return {
    apiKey,
    apiUrl: script.getAttribute('data-api-url') || undefined,
    googleConsentMode: script.getAttribute('data-google-consent-mode') === 'true',
  };
}

function resolveInitConfig(): ConsentPopInitConfig | undefined {
  const windowConfig = (window as any).ConsentPopConfig as ConsentPopInitConfig | undefined;
  if (windowConfig?.apiKey) return windowConfig;

  const currentConfig = configFromScript(document.currentScript as HTMLScriptElement | null);
  if (currentConfig) return currentConfig;

  const scripts = document.querySelectorAll<HTMLScriptElement>('script[data-api-key]');
  return configFromScript(scripts[scripts.length - 1] ?? null);
}

// Export for ESM / bundler usage
export { ConsentPop };
export default ConsentPop;
export type { ConsentPopInitConfig } from './core/types';

// Auto-initialize in browser
if (typeof window !== 'undefined') {
  injectBaseStyles();

  // Expose globally for UMD builds
  (window as any).ConsentPop = globalConsentPop;
  const pageConfig = resolveInitConfig();

  // This hint is intentionally read from the installation snippet instead of
  // the remote response so denied defaults are queued before config fetching.
  if (pageConfig?.googleConsentMode) {
    new GtagIntegration().setDefaultDenied();
  }

  const autoInit = () => {
    try {
      const config = pageConfig ?? resolveInitConfig();

      if (!config?.apiKey) {
        // No config found — this is fine, user may initialize manually
        return;
      }

      const instance = new ConsentPop(config);
      activeInstance = instance;
      void instance.init().then(() => {
        if (instance.isInitialized() && pendingPreferencesOpen) {
          pendingPreferencesOpen = false;
          instance.showPreferences();
        }
        const eventName = instance.isInitialized()
          ? 'consentpop:ready'
          : 'consentpop:error';
        window.dispatchEvent(new Event(eventName));
      });
    } catch {
      // Never break the customer's page
    }
  };

  // Init when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}
